import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { useToast } from '../common/Toast'
import { UserAvatar } from '../layout/UserAvatar'
import { resizeImage, isValidImageType, MAX_FILE_SIZE } from '../../utils/imageResize'

interface PhotoUploaderProps {
  currentPhotoUrl?: string
  name: string
  target: 'caregiver' | 'lovedOne'
  lovedOneId?: Id<'lovedOnes'>
  disabled?: boolean
}

export function PhotoUploader({
  currentPhotoUrl,
  name,
  target,
  lovedOneId,
  disabled = false,
}: PhotoUploaderProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const generateUploadUrl = useMutation(api.storage.generateUploadUrl)
  const saveProfilePhoto = useMutation(api.settings.saveProfilePhoto)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!isValidImageType(file)) {
      showToast({ message: t('settings.invalidImageType'), type: 'error' })
      return
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      showToast({ message: t('settings.imageTooLarge'), type: 'error' })
      return
    }

    setIsUploading(true)

    try {
      // Create preview
      const preview = URL.createObjectURL(file)
      setPreviewUrl(preview)

      // Resize image
      const resizedBlob = await resizeImage(file, 400, 0.85)

      // Get upload URL from Convex
      const uploadUrl = await generateUploadUrl()

      // Upload to Convex storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': resizedBlob.type },
        body: resizedBlob,
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      const { storageId } = await response.json()

      // Save photo reference
      await saveProfilePhoto({ storageId, target, lovedOneId })

      showToast({ message: t('settings.photoSaved'), type: 'success' })
    } catch (error) {
      console.error('Photo upload error:', error)
      showToast({ message: t('settings.photoUploadFailed'), type: 'error' })
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  }

  const uploadAreaStyles: React.CSSProperties = {
    position: 'relative',
    cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  }

  const overlayStyles: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: isUploading ? 1 : 0,
    transition: 'opacity 0.2s',
  }

  const buttonStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#4F46E5',
    background: 'none',
    border: 'none',
    cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
    padding: '0.5rem',
    minHeight: '44px',
    minWidth: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const displayPhotoUrl = previewUrl || currentPhotoUrl

  return (
    <div style={containerStyles}>
      <div style={uploadAreaStyles} onClick={handleClick}>
        <UserAvatar name={name} photoUrl={displayPhotoUrl} size="lg" />
        <div style={overlayStyles}>
          {isUploading && (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          )}
        </div>
      </div>

      <button
        type="button"
        style={buttonStyles}
        onClick={handleClick}
        disabled={disabled || isUploading}
      >
        {isUploading ? t('common.uploading') : t('settings.changePhoto')}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-label={t('settings.uploadPhoto')}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
